/**
 * Legal Certificate Page — /legal/certificate/:id
 *
 * Public route (no auth required). Fetches the immutable consent certificate
 * from the backend API and renders it inline.
 *
 * This page exists so the certificate URL in legal acceptance emails is always
 * a branded tenant URL ({slug}.euphoricai.io/legal/certificate/:id) and never
 * exposes the raw backend infrastructure domain (onrender.com).
 */
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function LegalCertificatePage() {
  const { id }           = useParams();
  const [html, setHtml]  = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!id) { setError('Invalid certificate ID.'); setLoading(false); return; }

    fetch(`${BACKEND_URL}/api/legal/certificate/${id}`)
      .then(res => {
        if (!res.ok) throw new Error(`Certificate not found (${res.status})`);
        return res.text();
      })
      .then(text => { setHtml(text); setLoading(false); })
      .catch(err => { setError(err.message); setLoading(false); });
  }, [id]);

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh', display: 'flex', alignItems: 'center',
        justifyContent: 'center', background: '#f1f5f9',
        fontFamily: 'Inter, -apple-system, sans-serif',
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: 40, height: 40, border: '3px solid #e2e8f0',
            borderTopColor: '#42a4bf', borderRadius: '50%',
            animation: 'spin 0.8s linear infinite', margin: '0 auto 16px',
          }} />
          <p style={{ color: '#64748b', fontSize: 14 }}>Loading certificate…</p>
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        minHeight: '100vh', display: 'flex', alignItems: 'center',
        justifyContent: 'center', background: '#f1f5f9',
        fontFamily: 'Inter, -apple-system, sans-serif',
      }}>
        <div style={{
          background: '#fff', borderRadius: 12, padding: '40px 48px',
          boxShadow: '0 4px 24px rgba(0,0,0,0.06)', textAlign: 'center', maxWidth: 480,
        }}>
          <p style={{ fontSize: 40, margin: '0 0 16px' }}>🔒</p>
          <h2 style={{ margin: '0 0 8px', color: '#0f172a' }}>Certificate Not Found</h2>
          <p style={{ color: '#64748b', fontSize: 14 }}>{error}</p>
          <p style={{ color: '#94a3b8', fontSize: 13, marginTop: 12 }}>
            This certificate ID may be invalid or the record may not exist.
            Contact <a href="mailto:legal@euphoricai.io" style={{ color: '#42a4bf' }}>legal@euphoricai.io</a> for assistance.
          </p>
        </div>
      </div>
    );
  }

  // Render the full certificate HTML returned by the backend
  // eslint-disable-next-line react/no-danger
  return <div dangerouslySetInnerHTML={{ __html: html }} />;
}
